import React, { useState } from 'react';
import { DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { exportConversations } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import DatePicker from './DatePicker';
import { generateExcelFile } from './scripts/generateExcelFile';

const ConversationExport = () => {
  const { clid } = useAuth();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dateError, setDateError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setDateError(null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setDateError(null);
  };

  const handlePresetSelect = (presetLabel) => {
    toast.success(`Selected: ${presetLabel}`);
  };

  const validateForm = () => {
    if (!startDate) {
      setDateError('Start date is required');
      return false;
    }
    if (!endDate) {
      setDateError('End date is required');
      return false;
    }

    if (startDate > endDate) {
      setDateError('Start date must be before end date');
      return false;
    }

    // Check if date range is too large (more than 1 year)
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      setDateError('Date range cannot exceed 365 days');
      return false;
    }

    return true;
  };

  const handleExport = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const exportData = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };
      const response = await exportConversations(exportData);

      if (response.success) {
        toast.success(response.message);

        // Create and download Excel file with multiple tabs
        const textMessages = response.data.text_messages || [];
        const voiceMails = response.data.voice_mails || [];
        const analytics = response.data.analytics;

        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];

        // Generate Excel file with multiple sheets
        generateExcelFile(textMessages, voiceMails, analytics, startDateStr, endDateStr, clid);

        // Show analytics summary if available
        if (analytics) {
          setAnalyticsData(analytics);
          showAnalyticsSummary(analytics);
        }
      } else {
        toast.error(response.message || 'Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      const isTimeout =
        error?.code === 'ECONNABORTED' ||
        /timeout/i.test(error?.message || '');
      if (isTimeout) {
        toast.error('Export timed out. Try a smaller date range.');
      } else {
        toast.error('Failed to export conversations');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showAnalyticsSummary = (analytics) => {
    const summary = `
📊 Export Summary:
• Total Conversations: ${analytics.total_conversations}
• Conversations with Reply: ${analytics.conversations_with_reply}
• Reply Rate: ${(analytics.conversations_with_reply_percentage * 100).toFixed(1)}%
• Voice Mails: ${analytics.voice_mails_count}
• Voice Mails with Reply: ${analytics.voice_mails_with_reply_count}
• Voice Mail Reply Rate: ${(analytics.voice_mails_with_reply_percentage * 100).toFixed(1)}%
    `;

    toast.success(summary, {
      duration: 8000,
      style: {
        whiteSpace: 'pre-line',
        fontSize: '14px'
      }
    });
  };


  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Export Conversations</h2>
        <p className="text-gray-600">Export conversation data to Excel with separate tabs for analytics, text messages, and voice mails</p>
      </div>

      {/* Date Picker */}
      <DatePicker
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onPresetSelect={handlePresetSelect}
        error={dateError}
        className="mb-6"
      />

      {/* Analytics Summary */}
      {analyticsData && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">📊 Export Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded border">
              <p className="text-sm text-gray-600">Total Conversations</p>
              <p className="text-xl font-bold text-blue-900">{analyticsData.total_conversations}</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-sm text-gray-600">With Reply</p>
              <p className="text-xl font-bold text-green-900">{analyticsData.conversations_with_reply}</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-sm text-gray-600">Reply Rate</p>
              <p className="text-xl font-bold text-purple-900">
                {(analyticsData.conversations_with_reply_percentage * 100).toFixed(1)}%
              </p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-sm text-gray-600">Voice Mails</p>
              <p className="text-xl font-bold text-orange-900">{analyticsData.voice_mails_count}</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-sm text-gray-600">Voice Mails with Reply</p>
              <p className="text-xl font-bold text-indigo-900">{analyticsData.voice_mails_with_reply_count}</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="text-sm text-gray-600">Voice Mail Reply Rate</p>
              <p className="text-xl font-bold text-pink-900">
                {(analyticsData.voice_mails_with_reply_percentage * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Export Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleExport}
          disabled={isLoading}
          className={`${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-[#075e54] hover:bg-[#064a42]'
          } text-white px-6 py-2 rounded-md font-medium flex items-center space-x-2 transition-colors`}
        >
          {isLoading ? (
            <ArrowPathIcon className="h-5 w-5 animate-spin" />
          ) : (
          <DocumentArrowDownIcon className="h-5 w-5" />
          )}
          <span>{isLoading ? 'Exporting...' : 'Export to Excel'}</span>
        </button>
      </div>
    </div>
  );
};

export default ConversationExport;
